import {Box, Flex, P} from 'exsportia-components'
import React, {FC, useCallback, useMemo} from 'react'

import {useBookingRainCheck} from '../../../api/booking/get-booking-rain-check'
import {TranslatorScope} from '../../../locale/types'
import {theme} from '../../../theme/theme'
import {Button} from '../../../ui/components/button/button'
import {useLocale} from '../../../utils/i18n'
import {
  formatRainCheckMoney,
  formatRainCheckTime,
  getRainCheckCardContent,
  RainCheckCardContent,
  RainCheckLine,
} from './rain-check-content'

type TranslateFn = (scope: TranslatorScope, options?: Record<string, number | string>) => string

const say = (l: TranslateFn, line: RainCheckLine) => l(line.key, line.params)

type ViewProps = {
  content: RainCheckCardContent
  l: TranslateFn
  isBusy?: boolean
  onCancel: () => void
  onContactVenue: () => void
}

// The card as a picture: it prints the content and reports a tap. The button runs the page's ordinary
// cancel flow (the same confirmation modal and saga as the Cancel button), never a rain-check route.
export const RainCheckCardView: FC<ViewProps> = ({content, l, isBusy = false, onCancel, onContactVenue}) => {
  const isDone = content.tone === 'done'
  return (
    <Box
      data-testid='rain-check-card'
      mb='16px'
      borderRadius='12px'
      overflow='hidden'
      border={`1px solid ${isDone ? theme.colors.success : theme.colors.notify}`}
    >
      <Box
        p='12px 16px'
        backgroundColor={isDone ? theme.colors.green_halftone : theme.colors.yellow_halftone}
      >
        <P
          fontSize='h3'
          fontWeight='bold'
        >
          {l(content.titleKey)}
        </P>
        {content.reason && <P fontSize='body'>{content.reason}</P>}
        {content.endsLine && (
          <P
            fontSize='body'
            color='text-secondary'
          >
            {say(l, content.endsLine)}
          </P>
        )}
      </Box>
      <Flex
        p='12px 16px'
        gap='12px'
        flexDirection='column'
      >
        {content.policyText && <P fontSize='body'>{content.policyText}</P>}
        {content.bookingName && (
          <Box>
            <P
              fontSize='body'
              fontWeight='bold'
            >
              {content.bookingName}
            </P>
            {content.bookingWhen && (
              <P
                fontSize='body'
                color='text-secondary'
              >
                {content.bookingWhen}
              </P>
            )}
          </Box>
        )}
        {content.moneyLine && (
          <P
            fontSize='body'
            fontWeight='bold'
          >
            {say(l, content.moneyLine)}
          </P>
        )}
        {content.shareReason && (
          <P
            fontSize='body'
            color='text-secondary'
          >
            {content.shareReason}
          </P>
        )}
        {content.body && <P fontSize='body'>{say(l, content.body)}</P>}
        {content.action && (
          <Button
            type={content.action.kind === 'cancel' ? 'primary' : 'secondary'}
            isLoading={isBusy}
            onClick={content.action.kind === 'cancel' ? onCancel : onContactVenue}
          >
            {l(content.action.labelKey)}
          </Button>
        )}
      </Flex>
    </Box>
  )
}

type Props = {
  bookingId: string
  isLoggedIn: boolean
  venueTimeZone?: string | null
  currency?: string | null
  bookingName?: string | null
  bookingWhen?: string | null
  isBusy?: boolean
  onCancel: () => void
  onContactVenue: () => void
}

// Quiet by design: no loading text, no error, no card while the answer is "none", unknown or failed.
// The ordinary Cancel button below it is always there.
export const RainCheckCard: FC<Props> = ({
  bookingId,
  isLoggedIn,
  venueTimeZone,
  currency,
  bookingName,
  bookingWhen,
  isBusy,
  onCancel,
  onContactVenue,
}) => {
  const {l} = useLocale()
  const {data: offer} = useBookingRainCheck(bookingId, isLoggedIn)

  const content = useMemo(
    () =>
      getRainCheckCardContent(offer, {
        formatMoney: (value, offerCurrency) => formatRainCheckMoney(l, value, offerCurrency || currency),
        formatTime: (value) => formatRainCheckTime(value, venueTimeZone),
        fallbackBookingName: bookingName,
        fallbackBookingWhen: bookingWhen,
      }),
    [offer, l, currency, venueTimeZone, bookingName, bookingWhen],
  )

  const handleCancel = useCallback(() => onCancel(), [onCancel])

  if (!content) {
    return null
  }
  return (
    <RainCheckCardView
      content={content}
      l={l}
      isBusy={isBusy}
      onCancel={handleCancel}
      onContactVenue={onContactVenue}
    />
  )
}
